import { getCsrfToken } from '../services/arcgisProxy';
import { FEEDBACK_ENDPOINT, type FeedbackType } from './constants';

/**
 * Request body for POST /api/feedback. Field names and shape mirror the manual
 * validation in apps/server/src/feedback/feedback.controller.ts
 * (`parseSubmission` / `validateContext`); the reporter identity is added
 * server-side from the session, never sent by the client.
 */
export interface FeedbackPayload {
  type: FeedbackType;
  title: string;
  description: string;
  stepsToReproduce?: string;
  screenshot: string;
  consoleLog: string;
  context: {
    pageUrl: string;
    viewport: { center: [number, number]; zoom: number };
    userAgent: string;
    appVersion: string;
  };
}

/** Raised when the server rejects a submission; `message` is user-facing. */
export class FeedbackSubmitError extends Error {}

const GENERIC_SUBMIT_ERROR = 'We could not submit your feedback. Please try again.';

export async function submitFeedback(payload: FeedbackPayload): Promise<void> {
  const response = await fetch(FEEDBACK_ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-XSRF-TOKEN': getCsrfToken(),
    },
    body: JSON.stringify(payload),
  });

  if (response.ok) return;

  let message = GENERIC_SUBMIT_ERROR;
  if (response.headers.get('content-type')?.includes('application/json')) {
    const body: unknown = await response.json();
    if (body !== null && typeof body === 'object' && 'message' in body) {
      const serverMessage = (body as { message: unknown }).message;
      if (typeof serverMessage === 'string' && serverMessage.length > 0) {
        message = serverMessage;
      } else if (Array.isArray(serverMessage) && serverMessage.length > 0) {
        message = serverMessage.join(' ');
      }
    }
  }
  throw new FeedbackSubmitError(message);
}
